import { nl } from 'date-fns/locale';

/**
 * Centralized constants for the application
 * This file contains shared configuration values, labels, and other constants
 */

// ==================== Localization ====================

/**
 * Default locale for date formatting
 */
export const DEFAULT_LOCALE = nl;

/**
 * Supported languages (Dutch only)
 */
export const SUPPORTED_LANGUAGES = ['nl'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// ==================== Date Formats ====================

export const DATE_FORMATS = {
  /** Short date: "5 jan" */
  SHORT: 'd MMM',
  /** Medium date: "5 jan 2024" */
  MEDIUM: 'd MMM yyyy',
  /** Long date: "5 januari 2024" */
  LONG: 'd MMMM yyyy',
  /** With time: "5 jan 2024 14:30" */
  WITH_TIME: 'd MMM yyyy HH:mm',
  /** Long with time: "5 januari 2024 14:30" */
  LONG_WITH_TIME: 'd MMMM yyyy HH:mm',
} as const;

// ==================== Geographic ====================

/**
 * Default coordinates for the Netherlands (center point)
 */
export const NETHERLANDS_CENTER = {
  lat: 52.1326,
  lng: 5.2913,
} as const;

/**
 * Common Dutch cities with their coordinates
 */
export const DUTCH_CITIES = {
  amsterdam: { lat: 52.3676, lng: 4.9041 },
  rotterdam: { lat: 51.9244, lng: 4.4777 },
  utrecht: { lat: 52.0907, lng: 5.1214 },
  denHaag: { lat: 52.0705, lng: 4.3007 },
  eindhoven: { lat: 51.4416, lng: 5.4697 },
  groningen: { lat: 53.2194, lng: 6.5665 },
  tilburg: { lat: 51.5555, lng: 5.0913 },
  almere: { lat: 52.3508, lng: 5.2647 },
  breda: { lat: 51.5719, lng: 4.7683 },
  nijmegen: { lat: 51.8426, lng: 5.8546 },
} as const;

// ==================== Pagination ====================

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// ==================== Form Validation ====================

export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_REVIEW_LENGTH: 2000,
  MAX_NAME_LENGTH: 100,
  MAX_EMAIL_LENGTH: 255,
  KVK_LENGTH: 8,
} as const;

// ==================== API & Rate Limiting ====================

export const API = {
  DEFAULT_TIMEOUT: 10000,
  MAX_RETRIES: 3,
  RATE_LIMIT_WINDOW_MS: 60000,
} as const;

// ==================== UI ====================

export const UI = {
  TOAST_DURATION: 5000,
  DEBOUNCE_MS: 300,
  ANIMATION_DURATION_MS: 200,
} as const;

// ==================== Platform Config ====================

export const PLATFORM = {
  /** Max response time (hours) before recommending improvement */
  RESPONSE_TIME_THRESHOLD_HOURS: 4,
  /** Days before an MSP profile is considered stale */
  PROFILE_STALE_DAYS: 90,
  /** Days of inactivity before MKB is considered inactive */
  MKB_ACTIVE_DAYS: 30,
  /** Throttle interval for last_active_at updates (ms) */
  ACTIVITY_THROTTLE_MS: 30 * 60 * 1000, // 30 minutes
} as const;

// ==================== Lead Scoring ====================

export const LEAD_SCORE = {
  HIGH_THRESHOLD: 70,
  MEDIUM_THRESHOLD: 40,
} as const;

export const LEAD_SCORE_LABELS = {
  HIGH: 'Hoog',
  MEDIUM: 'Gemiddeld',
  LOW: 'Laag',
} as const;

// ==================== MSP Related ====================

export const MSP = {
  /** Minimum medewerkers for enterprise segment */
  ENTERPRISE_MIN_EMPLOYEES: 50,
  /** Default response time in hours */
  DEFAULT_RESPONSE_TIME_HOURS: 24,
} as const;

// ==================== Storage Keys ====================

export const STORAGE_KEYS = {
  THEME: 'theme',
  LANGUAGE: 'language',
  COMPARE_MSPS: 'compare-msps',
  WIZARD_STATE: 'wizard-state',
} as const;

// ==================== Route Paths ====================

export const ROUTES = {
  HOME: '/',
  VERGELIJKEN: '/vergelijken',
  WIZARD: '/wizard',
  ACADEMY: '/academy',
  FAQ: '/faq',
  CONTACT: '/contact',
  OVER_ONS: '/over-ons',
  ZO_WERKT_HET: '/zo-werkt-het',
  VOOR_MSPS: '/voor-msps',
  AUTH: '/auth',
  PRIVACY: '/privacy',
  VOORWAARDEN: '/voorwaarden',
  COOKIES: '/cookies',
  // Dashboard routes
  DASHBOARD: {
    HOME: '/dashboard',
    PROFIEL: '/dashboard/profiel',
    LEADS: '/dashboard/leads',
    STATISTIEKEN: '/dashboard/statistieken',
    MIJN_MSP: '/dashboard/mijn-msp',
    ABONNEMENT: '/dashboard/abonnement',
    ACCOUNT: '/dashboard/account',
  },
  // Admin routes
  ADMIN: {
    DASHBOARD: '/admin',
    SETTINGS: '/admin/settings',
    CLAIMS: '/admin/claims',
    MSP_FORM: '/admin/msp',
    ARTIKEL_FORM: '/admin/artikel',
    PAGE_BUILDER: '/admin/page-builder',
  },
} as const;

// ==================== External URLs ====================

export const EXTERNAL_URLS = {
  MAPBOX_STYLE: 'mapbox://styles/mapbox/light-v11',
} as const;

// ==================== Review System ====================

/**
 * Industry options - SINGLE SOURCE OF TRUTH
 * Used by: Match Maker wizard (branche question), Review form
 */
export const INDUSTRY_OPTIONS = [
  { value: 'accountancy', label: 'Accountancy & Administratie' },
  { value: 'advocatuur', label: 'Advocatuur & Juridisch' },
  { value: 'financieel', label: 'Financiële dienstverlening' },
  { value: 'zorg', label: 'Gezondheidszorg' },
  { value: 'zakelijk', label: 'Zakelijke dienstverlening' },
  { value: 'overheid', label: 'Overheid & Onderwijs' },
  { value: 'techniek', label: 'Techniek & Productie' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'overig', label: 'Overige' },
] as const;

export type IndustryValue = typeof INDUSTRY_OPTIONS[number]['value'];

// ==================== Employee Segment Taxonomy (Platform-wide SSOT) ====================

/**
 * Canonical employee segment taxonomy.
 * Used by: MSP Portal doelgroep, overview filters, wizard matching, comparison matrix.
 */
export const EMPLOYEE_SEGMENTS = [
  { value: 'zzp',       label: 'ZZP / Freelancer' },
  { value: '2_10',      label: '2–10 medewerkers' },
  { value: '11_25',     label: '11–25 medewerkers' },
  { value: '26_50',     label: '26–50 medewerkers' },
  { value: '51_100',    label: '51–100 medewerkers' },
  { value: '101_250',   label: '101–250 medewerkers' },
  { value: '250_plus',  label: '250+ medewerkers' },
] as const;

export type EmployeeSegmentValue = typeof EMPLOYEE_SEGMENTS[number]['value'];

/**
 * Get display label for an employee segment value.
 */
export function getEmployeeSegmentLabel(value: string): string {
  const found = EMPLOYEE_SEGMENTS.find(o => o.value === value);
  return found ? found.label : value;
}

// ==================== Response Time Options ====================

/**
 * Canonical response time tier options with Dutch labels.
 *
 * ⚠️ SINGLE SOURCE OF TRUTH ⚠️
 * This list is mirrored by the DB trigger `public.validate_msps_new_fields()`
 * which whitelists the same values for the `msps.response_time_tier` column.
 * If you add, rename, or remove a tier here, you MUST ship a migration that
 * updates the trigger's IN-list in the same change. Otherwise profile saves
 * with the new value will fail with `Invalid response_time_tier: ...`.
 */
export const RESPONSE_TIME_OPTIONS = [
  { value: '<1h',               label: 'Binnen 1 uur' },
  { value: '<4h',               label: 'Binnen 4 uur' },
  { value: 'next_business_day', label: 'Volgende werkdag' },
  { value: '2_business_days',   label: 'Binnen 2 werkdagen' },
  { value: 'on_request',        label: 'Op afspraak / afhankelijk van vraag' },
] as const;

/**
 * Get Dutch label for a response_time_tier value.
 */
/**
 * Fallback mapping for non-canonical response_time values.
 * Ensures we never show raw English enum keys in the UI.
 */
const RESPONSE_TIME_FALLBACKS: Record<string, string> = {
  'same_day': 'Dezelfde dag',
  'next_business_day': 'Volgende werkdag',
  'within_1_hour': 'Binnen 1 uur',
  'within_4_hours': 'Binnen 4 uur',
  'within_8_hours': 'Binnen 8 uur',
  'binnen 1 uur': 'Binnen 1 uur',
  'binnen 4 uur': 'Binnen 4 uur',
  'binnen 8 uur': 'Binnen 8 uur',
  'binnen 1 werkdag': 'Volgende werkdag',
  'binnen 2 werkdagen': 'Binnen 2 werkdagen',
  'volgende_werkdag': 'Volgende werkdag',
  'dezelfde_dag': 'Dezelfde dag',
};

export function getResponseTimeTierLabel(value: string): string {
  if (!value) return '—';
  const found = RESPONSE_TIME_OPTIONS.find(o => o.value === value);
  if (found) return found.label;
  const fallback = RESPONSE_TIME_FALLBACKS[value.toLowerCase()];
  return fallback || '—';
}

// ==================== NIS2 Service Classification ====================

/**
 * Derive NIS2 service classification status from MSP capability data.
 * - 'geen': no NIS2 capabilities declared
 * - 'dienstverlening': self-declared NIS2/SC capabilities (from toggles)
 * - 'gecertificeerd': verified NIS2 certificate on file
 *
 * NOTE: SC level reflects maturity, not service offering.
 * dienstverlening is derived from capability toggles, NOT SC level.
 */
export type NIS2ServiceStatus = 'geen' | 'dienstverlening' | 'gecertificeerd';

export const NIS2_SERVICE_STATUS_OPTIONS = [
  { value: 'geen',              label: 'Geen' },
  { value: 'dienstverlening',   label: 'NIS2 dienstverlening' },
  { value: 'gecertificeerd',    label: 'NIS2 gecertificeerd' },
] as const;

/**
 * Derive NIS2 service status from MSP data.
 * @param msp - MSP record with NIS2 flags and optionally verified certs
 * @param hasVerifiedNIS2Cert - whether this MSP has a verified NIS2 certificate
 */
export function deriveNIS2ServiceStatus(
  msp: Record<string, any>,
  hasVerifiedNIS2Cert = false
): NIS2ServiceStatus {
  if (hasVerifiedNIS2Cert) return 'gecertificeerd';

  const hasNIS2Capabilities = [
    msp.nis2_werkplek_identity, msp.nis2_cloud_infra,
    msp.nis2_support_incident, msp.nis2_backup_bcdr,
    msp.nis2_patch_vulnerability, msp.nis2_network_access,
    msp.nis2_detection_monitoring, msp.nis2_security_awareness,
  ].some(Boolean);

  const hasSupplyChainCapabilities = [
    msp.nis2_incident_procedure_documented, msp.nis2_incident_procedure_tested,
    msp.nis2_vulnerabilities_prioritized, msp.nis2_network_segmentation,
    msp.nis2_logging_centralized, msp.nis2_alerts_followed_up,
    msp.nis2_training_yearly, msp.nis2_results_measured,
  ].some(Boolean);

  if (hasNIS2Capabilities || hasSupplyChainCapabilities) return 'dienstverlening';
  return 'geen';
}

/**
 * SC Level inheritance: higher levels include all lower levels.
 * When filtering for SC10, show SC10 + SC20 + SC30.
 */
export function scLevelMatchesFilter(mspLevel: string | null, filterLevel: string): boolean {
  const hierarchy: Record<string, number> = { 'SC10': 1, 'SC20': 2, 'SC30': 3 };
  const mspRank = hierarchy[mspLevel || ''] || 0;
  const filterRank = hierarchy[filterLevel] || 0;
  return mspRank >= filterRank;
}

// ==================== Company Size Options (Reviews — backward compat) ====================

/**
 * Company size options for reviews
 */
export const COMPANY_SIZE_OPTIONS = [
  { value: 'zzp', label: 'ZZP / 1 persoon' },
  { value: '2-5', label: '2-5 medewerkers' },
  { value: '6-10', label: '6-10 medewerkers' },
  { value: '11-25', label: '11-25 medewerkers' },
  { value: '26-50', label: '26-50 medewerkers' },
  { value: '50+', label: '50+ medewerkers' },
] as const;

export type CompanySizeValue = typeof COMPANY_SIZE_OPTIONS[number]['value'];

// Legacy mapping for old stored values
const LEGACY_COMPANY_SIZE_MAP: Record<string, string> = {
  '2-10': '2-5 medewerkers',
  '51-100': '50+ medewerkers',
  '100+': '50+ medewerkers',
};

/**
 * Get display label for a company size value, with legacy fallback.
 * Never exposes raw legacy strings on public pages.
 */
export function getCompanySizeLabel(value: string): string {
  const found = COMPANY_SIZE_OPTIONS.find(o => o.value === value);
  if (found) return found.label;
  return LEGACY_COMPANY_SIZE_MAP[value] || value;
}

// ==================== Engagement Model Labels ====================

export const ENGAGEMENT_MODEL_LABELS: Record<string, string> = {
  'full_outsource': 'Managed IT (outsourcing)',
  'co_managed': 'Co-managed IT',
  'project_only': 'IT projecten',
  'support_only': 'IT support / helpdesk',
};

/**
 * Get display label for an industry value.
 */
export function getIndustryLabel(value: string): string {
  const found = INDUSTRY_OPTIONS.find(o => o.value === value);
  return found ? found.label : value;
}

/**
 * Review flag reasons (for MSP flagging)
 */
export const REVIEW_FLAG_REASONS = [
  { value: 'incorrect', label: 'Feitelijk onjuist' },
  { value: 'spam', label: 'Spam / reclame' },
  { value: 'inappropriate', label: 'Ongepaste inhoud' },
  { value: 'fake', label: 'Nep review / geen klant' },
  { value: 'duplicate', label: 'Dubbele review' },
  { value: 'other', label: 'Overig' },
] as const;

export type ReviewFlagReason = typeof REVIEW_FLAG_REASONS[number]['value'];

// ==================== Blocked Email Domains (Client-side UX only) ====================

/**
 * Client-side list of common public email domains.
 * Used for UX feedback only — real enforcement is server-side.
 */
export const BLOCKED_EMAIL_DOMAINS_CLIENT = [
  'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com',
  'icloud.com', 'protonmail.com', 'live.com', 'msn.com',
  'aol.com', 'mail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'fastmail.com',
] as const;

// ==================== Relationship Type Options ====================

export const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'current_client', label: 'Huidige klant' },
  { value: 'client_last_12_months', label: 'Klant afgelopen 12 maanden' },
  { value: 'older_client', label: 'Voormalige klant (ouder dan 12 maanden)' },
] as const;

export type RelationshipTypeValue = typeof RELATIONSHIP_TYPE_OPTIONS[number]['value'];

// ==================== NIS2 Public View Columns ====================

/**
 * Canonical list of NIS2 columns exposed in msps_public view.
 * Used as documentation constant to prevent query/rendering drift.
 */
export const MSP_PUBLIC_NIS2_COLUMNS = [
  'nis2_werkplek_identity',
  'nis2_support_incident',
  'nis2_backup_bcdr',
  'nis2_security_awareness',
  'nis2_cloud_infra',
  'nis2_patch_vulnerability',
  'nis2_network_access',
  'nis2_detection_monitoring',
  'nis2_ondersteuning',
  'nis2_beschrijving',
] as const;

// ==================== Certification Verification ====================

/**
 * Certifications that require document upload and admin verification.
 */
export const VERIFIABLE_CERTIFICATIONS = [
  'ISO27001', 'ISO9001', 'NEN7510', 'Microsoft Partner', 'Cyber Essentials', 'CISSP',
  'NIS2 (SC Readiness)', 'NIS2 / Supply Chain 10', 'NIS2 / Supply Chain 20', 'NIS2 / Supply Chain 30 (SC30)',
  'Keurmerk Digitale Basisveiligheid MKB',
] as const;

/**
 * Certifications that require upload but follow a different verification flow.
 */
export const UPLOAD_ONLY_CERTIFICATIONS = ['SOC2'] as const;

// ==================== NIS2 Certification Mapping ====================

/**
 * Canonical NIS2 certification levels.
 * READINESS is a separate level from SC10.
 */
export type NIS2CertLevel = 'READINESS' | 'SC10' | 'SC20' | 'SC30';

/**
 * Maps certification_type strings to canonical NIS2 levels.
 * Used for determining official NIS2 status from verified certificates.
 */
export const NIS2_CERT_TYPE_TO_LEVEL: Record<string, NIS2CertLevel> = {
  'NIS2 (SC Readiness)': 'READINESS',
  'NIS2 / Supply Chain 10': 'SC10',
  'NIS2 / Supply Chain 20': 'SC20',
  'NIS2 / Supply Chain 30 (SC30)': 'SC30',
};

/**
 * Coverage rules: higher levels explicitly cover lower levels.
 * SC30 → covers READINESS + SC10 + SC20 + SC30
 */
export const NIS2_LEVEL_COVERAGE: Record<NIS2CertLevel, NIS2CertLevel[]> = {
  READINESS: ['READINESS'],
  SC10: ['READINESS', 'SC10'],
  SC20: ['READINESS', 'SC10', 'SC20'],
  SC30: ['READINESS', 'SC10', 'SC20', 'SC30'],
};

/**
 * Canonical keys for certification name normalisation (deduplication).
 * Maps common variations of certification names to a single canonical key.
 */
export const CERT_CANONICAL_KEYS: Record<string, string> = {
  'iso27001': 'iso27001',
  'iso 27001': 'iso27001',
  'iso-27001': 'iso27001',
  'iso9001': 'iso9001',
  'iso 9001': 'iso9001',
  'iso-9001': 'iso9001',
  'nen7510': 'nen7510',
  'nen 7510': 'nen7510',
  'nen-7510': 'nen7510',
  'microsoft partner': 'microsoft_partner',
  'cyber essentials': 'cyber_essentials',
  'cissp': 'cissp',
  'soc2': 'soc2',
  'soc 2': 'soc2',
  'soc-2': 'soc2',
  'nis2 (sc readiness)': 'nis2_readiness',
  'nis2 / supply chain 10': 'nis2_sc10',
  'nis2 / supply chain 20': 'nis2_sc20',
  'nis2 / supply chain 30 (sc30)': 'nis2_sc30',
  'keurmerk digitale basisveiligheid mkb': 'keurmerk_digitale_basisveiligheid_mkb',
};

/**
 * Normalise a certification name to a canonical key for deduplication.
 */
export function normaliseCertName(name: string): string {
  const lower = name.trim().toLowerCase();
  return CERT_CANONICAL_KEYS[lower] || lower.replace(/[\s\-\/()]+/g, '_');
}

/**
 * Self-declared compliance claims — NOT stored in msp_certifications.
 * These stay on the MSP profile as boolean/text fields.
 */
export const SELF_DECLARED_CERTIFICATIONS = ['GDPR', 'DORA'] as const;

/**
 * Combined list of all certification types that can be uploaded.
 */
export const ALL_UPLOADABLE_CERTIFICATIONS = [
  ...VERIFIABLE_CERTIFICATIONS,
  ...UPLOAD_ONLY_CERTIFICATIONS,
] as const;

export type UploadableCertificationType = typeof ALL_UPLOADABLE_CERTIFICATIONS[number];

/**
 * Display labels for verification statuses.
 */
export const CERTIFICATION_STATUS_LABELS: Record<string, string> = {
  pending: 'In afwachting',
  verified: 'Geverifieerd',
  rejected: 'Afgewezen',
};
